import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  serviceGetConversationDetail,
  serviceGetConversationMessage,
  serviceSendMessage,
} from "../../../../../services/Chat.service";
import LogUtils from "../../../../../libs/LogUtils";
import { getSocketInstance } from "../../../../../libs/SocketUtils";
import Constants from "../../../../../config/constants";
import { useSelector } from "react-redux";
import { debounce } from "lodash";
import SnackbarUtils from "../../../../../libs/SnackbarUtils";
import history from "../../../../../libs/history.utils";
import RouteName from "../../../../../routes/Route.name";
import EventEmitter from "../../../../../libs/Events.utils";

const useChatHook = ({ chatUser, isAuditAdmin, setChatUser, isFloatingChat }) => {
  const [message, setMessage] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [conversationData, setConversationData] = useState({});
  const [chatMessage, setChatMessage] = useState([]);
  const [sidePanel, setSidePanel] = useState(false);
  const [isSocket, setIsSocket] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isDataFetched, setIsDataFetched] = useState(false);
  const fileInputRef = useRef(null);
  const prevScrollHeightRef = useRef(0);
  const isLoadingOlderMessages = useRef(false);
  const prefilledApplied = useRef(false);
  const { user } = useSelector((state) => state.auth);

  const token = useMemo(() => {
    return user?.token;
  }, [user?.token]);
  const socket = getSocketInstance();

  const chatWindowRef = useRef(null);

  const open = Boolean(anchorEl);
  const id = open ? "chat-popover" : undefined;

  const handlePopOverClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const scrollToBottom = useCallback(() => {
    if (chatWindowRef?.current) {
      setTimeout(() => {
        chatWindowRef?.current?.scrollTo({
          top: chatWindowRef?.current?.scrollHeight,
          behavior: "smooth",
        });
      }, 0);
    }
  }, []);

  const instantScrollToBottom = useCallback(() => {
    if (chatWindowRef?.current) {
      setTimeout(() => {
        chatWindowRef?.current?.scrollTo({
          top: chatWindowRef?.current?.scrollHeight,
          behavior: "auto",
        });
      }, 0);
    }
  }, []);

  useEffect(() => {
    if (conversationData?.conversation_id && !isFloatingChat) {
      history.push(
        `${
          isAuditAdmin ? RouteName?.AUDIT_GROUP : RouteName.MESSAGING
        }?conversationId=${conversationData?.conversation_id}`
      );
    }
  }, [conversationData?.conversation_id, isFloatingChat]);

  console.log("ChatUser in hook", chatUser);
  useEffect(() => {
    if (chatUser) {
      if (chatUser?.prefilledMessage && !prefilledApplied.current) {
        setMessage(chatUser.prefilledMessage);
        prefilledApplied.current = true;
      }
      setIsSubmitting(true);
      serviceGetConversationDetail(
        chatUser?.conversation_id
          ? { conversation_id: chatUser?.conversation_id }
          : { employee_id: chatUser?.id || chatUser?._id || chatUser?.employee_id }
      ).then((res) => {
        if (!res?.error) {
          setConversationData(res?.data);
        }
        setIsSubmitting(false);
        setIsDataFetched(true);
      });
    }
  }, [chatUser]);

  const chatParticipant = useMemo(
    () =>
      conversationData?.type === "GROUP"
        ? conversationData?.participants_meta ?? []
        : [],
    [conversationData]
  );

  const handleClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      if (selectedFiles.length + files.length > 3) {
        SnackbarUtils.error("You can only upload up to 3 files at a time.");
        if (fileInputRef.current) {
          fileInputRef.current.value = null;
        }
        return;
      }

      const allowedTypes = [
        "application/pdf",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/msword",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];

      const validFiles = files.filter(file => allowedTypes.includes(file.type));
      if (validFiles.length !== files.length) {
        SnackbarUtils.error(
          "Only PDF, Excel, Word, and Image (JPG, JPEG, PNG) files are allowed."
        );
      }
      
      setSelectedFiles(prev => [...prev, ...validFiles]);
      
      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }
    }
  };

  useEffect(() => {
    if (conversationData?.conversation_id) {
      getChatMessages(1, "instant");
    }
  }, [conversationData?.conversation_id]);

  // useEffect(() => {
  //   setTimeout(() => {
  //     scrollToBottom();
  //   }, 200);
  // }, []);

  const getChatMessages = useCallback(
    async (pageNum, scrollType) => {
      if (loading) return;
      setLoading(true);

      try {
        const res = await serviceGetConversationMessage({
          conversation_id: conversationData?.conversation_id,
          page_id: pageNum,
        });

        if (!res?.error && res?.data?.messages?.length) {
          setChatMessage((prevMessages) =>
            pageNum === 1
              ? [...res.data.messages]
              : [...res.data.messages, ...prevMessages]
          );
          setPage(pageNum);
          setHasMore(res?.data?.messages.length > 0);
        } else {
          setHasMore(false);
        }
      } catch (error) {
        console.error("Error fetching messages:", error);
      } finally {
        setLoading(false);
        setIsDataFetched(true);
        if (scrollType === "instant") {
          instantScrollToBottom();
        } else if (scrollType === "smooth") {
          scrollToBottom();
        }
      }
    },
    [conversationData?.conversation_id, loading]
  );

  const handleScroll = useCallback(
    debounce(() => {
      const chatContainer = chatWindowRef.current;
      if (chatContainer?.scrollTop === 0 && hasMore) {
        prevScrollHeightRef.current = chatContainer.scrollHeight; // Save scroll height before loading more messages
        isLoadingOlderMessages.current = true;
        getChatMessages(page + 1);
      }
    }, 500),
    [getChatMessages, hasMore, page]
  );

  useEffect(() => {
    if (chatWindowRef.current && isLoadingOlderMessages.current) {
      chatWindowRef.current.scrollTop =
        chatWindowRef.current.scrollHeight - prevScrollHeightRef.current;
      isLoadingOlderMessages.current = false;
    }
  }, [chatMessage]);

  useEffect(() => {
    if (chatWindowRef.current) {
      chatWindowRef?.current?.addEventListener("scroll", handleScroll);
      return () =>
        chatWindowRef?.current?.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll]);

  useEffect(() => {
    if (conversationData?.conversation_id && !isSocket) {
      setIsSocket(true);
      if (socket) {
        socket.emit(
          Constants.SOCKET_EVENT.SUBSCRIBE,
          conversationData?.conversation_id
        );
        window.CURRENT_SOCKET_ROOM = conversationData?.conversation_id;
        window.OPEN_SOCKET_ROOMS = window.OPEN_SOCKET_ROOMS || new Set();
        window.OPEN_SOCKET_ROOMS.add(conversationData.conversation_id);
      }
    }
  }, [conversationData]);

  const profileInfo = useMemo(() => {
    if (chatUser?.meta_data) {
      return chatUser?.meta_data;
    } else
      return {
        label: chatUser?.name || "-",
        description: chatUser?.code || "-",
        image: chatUser?.image || "-",
        initials: chatUser?.initials || "",
      };
  }, [chatUser]);

  const handleNewMessageFromServer = useCallback(
    (data) => {
      const incomingMsg = data?.message || data;
      if (!incomingMsg) return;

      // Ensure the incoming socket message belongs specifically to this chat's conversation
      if (
        conversationData?.conversation_id &&
        incomingMsg?.conversation_id &&
        String(incomingMsg.conversation_id) !== String(conversationData.conversation_id)
      ) {
        return;
      }

      setChatMessage((prevMessages) => {
        if (prevMessages.some((msg) => msg.id === incomingMsg?.id)) {
          return prevMessages;
        }
        return [...prevMessages, incomingMsg];
      });
      scrollToBottom();
    },
    [scrollToBottom, conversationData?.conversation_id]
  );

  useEffect(() => {
    return () => {
      if (socket && conversationData?.conversation_id) {
        socket.emit(
          Constants.SOCKET_EVENT.UNSUBSCRIBE,
          conversationData.conversation_id
        );
        if (window.OPEN_SOCKET_ROOMS) {
          window.OPEN_SOCKET_ROOMS.delete(conversationData.conversation_id);
        }
      }
      if (window.CURRENT_SOCKET_ROOM === conversationData?.conversation_id) {
        window.CURRENT_SOCKET_ROOM = null;
      }
    };
  }, [conversationData?.conversation_id]);

  useEffect(() => {
    if (!socket) return;

    const handleReconnect = () => {
      if (conversationData?.conversation_id) {
        socket.emit(
          Constants.SOCKET_EVENT.SUBSCRIBE,
          conversationData.conversation_id
        );
        console.log("Re-subscribed to room after reconnect");
      }
    };

    socket.on("connect", handleReconnect);

    return () => {
      socket.off("connect", handleReconnect);
    };
  }, [conversationData?.conversation_id, socket]);

  useEffect(() => {
    socket.on(
      Constants.SOCKET_EVENT.INDIVIDUAL_MESSAGE_CHANNEL,
      handleNewMessageFromServer
    );

    return () => {
      socket.removeListener(
        Constants.SOCKET_EVENT.INDIVIDUAL_MESSAGE_CHANNEL,
        handleNewMessageFromServer
      );
    };
  }, [handleNewMessageFromServer, message]);

  const handleChangeMessage = useCallback((message) => {
    setMessage(message);
  }, []);

  const sendMessageToServer = useCallback(async () => {
    if (!navigator.onLine) {
      SnackbarUtils.error("No internet connection.");
      return;
    }
    setIsSubmitting(true);
    try {
      if (selectedFiles.length > 0) {
        for (let i = 0; i < selectedFiles.length; i++) {
          const file = selectedFiles[i];
          const textMessage = i === 0 ? message : "";
          
          if (!isSocket) {
            const fd = new FormData();
            fd.append("receiver_id", chatUser?.id);
            fd.append("message", textMessage);
            fd.append("file", file);
            fd.append("file_mime", file.type);
            const res = await serviceSendMessage(fd);
            
            if (i === selectedFiles.length - 1) {
              setChatUser?.((prev) => ({ ...prev, prefilledMessage: undefined }));
              setConversationData({ ...res.data });
            }
            await getChatMessages(1, "smooth");
          } else {
            const fd = new FormData();
            fd.append("conversation_id", conversationData?.conversation_id);
            fd.append("message", textMessage);
            fd.append("file", file);
            fd.append("file_mime", file.type);
            await serviceSendMessage(fd);
            await getChatMessages(1, "smooth");
        EventEmitter.dispatch(EventEmitter.REFRESH_PENDING_COUNTS);
          }
        }
      } else {
        if (!isSocket) {
          const fd = new FormData();
          fd.append("receiver_id", chatUser?.id);
          fd.append("message", message);
          const res = await serviceSendMessage(fd);
          setChatUser?.((prev) => ({ ...prev, prefilledMessage: undefined }));
          setConversationData({ ...res.data });
          await getChatMessages(1, "smooth");
        } else {
          socket.emit(Constants.SOCKET_EVENT.EMIT_TEXT_MESSAGE, {
            message,
            conversation_id: conversationData?.conversation_id,
            token,
          });
        }
        EventEmitter.dispatch(EventEmitter.REFRESH_PENDING_COUNTS);
      }
      setMessage("");
      setSelectedFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }
      scrollToBottom();
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    message,
    isSocket,
    token,
    chatUser,
    selectedFiles,
    setMessage,
    setSelectedFiles,
    conversationData?.conversation_id,
    getChatMessages,
    scrollToBottom,
    socket,
  ]);

  const handleSendMessage = useCallback(
    (e) => {
      if (e.key === "Enter" && e.shiftKey) {
        e.preventDefault();
        setMessage((prev) => prev + "\n");
        return;
      }
      // if (e.key === "Enter") {
      //   e.preventDefault();
      //   if (!message.trim() && selectedFiles.length === 0) {
      //     SnackbarUtils.error("Please enter the text");
      //     return;
      //   }
      //   sendMessageToServer();
      // }
    },
    [message, selectedFiles, sendMessageToServer]
  );

  const handleMessageSend = useCallback(
    (e) => {
      e.preventDefault();
      if (!message.trim() && selectedFiles.length === 0) {
        SnackbarUtils.error("Please enter the text");
        return;
      }
      sendMessageToServer();
    },
    [message, selectedFiles, sendMessageToServer]
  );
  const isGroupChat = useMemo(() => {
    return conversationData?.type === "GROUP";
  }, [conversationData]);

  const isGroupAdmin = useMemo(() => {
    if (!conversationData?.meta_data?.is_close_group) return false;

    return conversationData?.participants_meta?.some(
      (participant) => participant?.is_admin && participant?.id === user?.id
    );
  }, [conversationData, user]);

  const isAdminCoAdmin = useMemo(()=>{
    return conversationData?.admin_ids?.includes(user?.id)
  },[conversationData,user])

  const isMessageDisabled = useMemo(() => {
    return (
      isGroupChat &&
      conversationData?.meta_data?.is_close_group &&
      !isGroupAdmin && !isAdminCoAdmin
    );
  }, [isGroupChat, conversationData?.meta_data?.is_close_group, isGroupAdmin,isAdminCoAdmin]);

  const handleSideToggle = useCallback(() => {
    setSidePanel((e) => !e);
    handleClose();
  }, [setSidePanel, handleClose]);

  const adminUser = useMemo(() => {
    return conversationData?.participants_meta?.some(
      (participant) => participant?.is_admin && participant?.id === user?.id
    );
  }, [conversationData, user]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (
        document.visibilityState === "visible" &&
        conversationData?.conversation_id
      ) {
        getChatMessages(1, "instant");
        if (
          socket &&
          window.CURRENT_SOCKET_ROOM !== conversationData.conversation_id
        ) {
          socket.emit(
            Constants.SOCKET_EVENT.SUBSCRIBE,
            conversationData.conversation_id
          );
          window.CURRENT_SOCKET_ROOM = conversationData.conversation_id;
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [conversationData?.conversation_id, getChatMessages, socket]);

  useEffect(() => {
    scrollToBottom();
  }, [scrollToBottom]);
  
  return {
    message,
    handleSendMessage,
    handleChangeMessage,
    profileInfo,
    conversationData,
    chatMessage,
    chatWindowRef,
    handleFileChange,
    handleClick,
    fileInputRef,
    chatParticipant,
    isGroupChat,
    open,
    id,
    anchorEl,
    handlePopOverClick,
    handleClose,
    sidePanel,
    handleSideToggle,
    loading,
    isMessageDisabled,
    isSubmitting,
    isGroupAdmin,
    adminUser,
    handleMessageSend,
    selectedFiles,
    setSelectedFiles,
    isDataFetched,
    isAdminCoAdmin
  };
};

export default useChatHook;
